import { type ReactNode, useId } from 'react'

import { OrderStatusChip } from '~/components/dashboard/clients/timeline/prescriptions/rxTracker/components/OrderStatusChip'
import i18n from '~/locales/i18n'

import type { OrderTableCardHeader, OrderTableRow } from '../orderTableTypes'
import {
  getChewyOrderInfoLabels,
  getPrescriptionFromRow,
} from '../rxCardUtilities'

import styles from './OrderTable.module.scss'

export type { OrderTableRow } from '../orderTableTypes'

export interface OrderTableProps {
  cardHeaders?: OrderTableCardHeader[]
  className?: string
  getRowKey?: (rowIndex: number, row: OrderTableRow[]) => string
  headers: string[]
  renderRowActions?: (row: OrderTableRow[]) => ReactNode
  rows: OrderTableRow[][]
  subHeader?: string | null
}

const emptyCell: Readonly<OrderTableRow> = {
  label: '',
  value: '',
  relativeTime: null,
}

const renderActionsZone = (children: ReactNode) => (
  <div data-click-exclude="true">{children}</div>
)

const generateRowKey = (
  componentId: string,
  cells: OrderTableRow[],
): string => {
  const prescription = getPrescriptionFromRow(cells)
  const prescriptionId = prescription?.id ?? ''
  return `${componentId}-${prescriptionId}`
}

export const OrderTable = ({
  cardHeaders,
  headers,
  rows,
  renderRowActions,
  getRowKey,
  subHeader,
  className,
}: OrderTableProps) => {
  const componentId = useId()

  if (!rows?.length || !headers?.length) return null

  return (
    <div className={className ?? styles.cardsContainer}>
      {rows.map((row, rowIndex) => {
        const rowKey = getRowKey
          ? getRowKey(rowIndex, row)
          : generateRowKey(componentId, row)

        const cardHeader = cardHeaders?.[rowIndex]
        const cardHeaderText =
          cardHeader?.text ?? subHeader ?? i18n.t('Common:ORDER')
        const statusTag = cardHeader?.status
        const showOrderAndPrescriptionNumbers =
          cardHeader?.showOrderAndPrescriptionNumbers ?? false
        const rowPrescription = getPrescriptionFromRow(row)

        return (
          <div className={styles.orderCard} key={rowKey}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderTitleRow}>
                <span className={styles.cardHeaderText}>{cardHeaderText}</span>
                {showOrderAndPrescriptionNumbers &&
                  rowPrescription &&
                  (() => {
                    const {
                      orderNumber: orderLabel,
                      prescriptionNumber: rxLabel,
                    } = getChewyOrderInfoLabels(rowPrescription)
                    const orderValue = cardHeader?.orderNumber ?? ''
                    const rxValue = cardHeader?.rxNo ?? ''
                    return (
                      <span className={styles.cardHeaderOrderInfo}>
                        <span>
                          {orderLabel}: {orderValue} {rxLabel}: {rxValue}
                        </span>
                      </span>
                    )
                  })()}
              </div>
              {statusTag && (
                <div className={styles.cardHeaderActions}>
                  <OrderStatusChip
                    className={styles.statusTag}
                    status={statusTag}
                  />
                </div>
              )}
            </div>
            <div className={styles.cardContent}>
              {headers.map((header, cellIndex) => {
                const cell = row[cellIndex] || emptyCell
                if (!cell.label && !cell.value) return null

                const fieldKey = cell.label || header

                return (
                  <div
                    className={styles.cardField}
                    key={`${rowKey}-field-${fieldKey}`}
                  >
                    <span className={styles.cardFieldLabel}>{cell.label}</span>
                    <div className={styles.cardFieldValue}>
                      <span className={styles.cardFieldText}>{cell.value}</span>
                      {cell.relativeTime && (
                        <span className={styles.cardFieldDateLabel}>
                          {cell.relativeTime}
                        </span>
                      )}
                      {cell.secondaryText?.trim() && (
                        <span
                          className={
                            cell.useSecondaryTextStyle
                              ? styles.cardFieldDateLabel
                              : styles.cardFieldSecondaryText
                          }
                          title={cell.secondaryText}
                        >
                          {cell.secondaryText}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
              {renderRowActions && (
                <div className={styles.cardActions}>
                  {renderActionsZone(renderRowActions(row))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
